import { P } from './projection'
import { CROWN } from './padShape'

/**
 * The ISOFIX cap (portfolio-new-4), computed once at module load: a moulded
 * rounded-square tile whose big face carries the mark. World x 1040..1180,
 * z 20..140, walls from the nest top (h 58); the face is a barrel crowned
 * along z, CROWN higher on its centre line (h 110, stage y 550 at z 80, the
 * pad's contact point) than at its side edges. Rounded corners (r 14); a
 * rolled perimeter band (6 wide) and a glint on its crest along all four
 * edges; walls with a roll band at the top and a flange at the foot.
 */

const X0 = 1040
const X1 = 1180
const Z0 = 20
const Z1 = 140
const XC = (X0 + X1) / 2
const BASE = 58
const TOP = 110

/** Face height at world x: the crown is a parabola across x. */
export const capH = (x) => TOP - CROWN * ((x - XC) / 70) ** 2

const r1 = (n) => Math.round(n * 10) / 10
const pt = ([x, y]) => `${r1(x)} ${r1(y)}`
const reverse = (pts) => [...pts].reverse()
const path = (points, close = true) => `M${points.map(pt).join('L')}${close ? 'Z' : ''}`
const onFace = ([x, z], drop = 0) => P(x, capH(x) - drop, z)

/** Rounded rectangle in world (x, z) as [x, z, angle], the angle unwrapped from 180 (left edge,
 *  front corner) round the front (270), the right edge (360) and the back (450) to 540. */
function roundedRect(inset, radius) {
  const [x0, x1, z0, z1] = [X0 + inset, X1 - inset, Z0 + inset, Z1 - inset]
  const corners = [
    [x0 + radius, z0 + radius, 180],
    [x1 - radius, z0 + radius, 270],
    [x1 - radius, z1 - radius, 360],
    [x0 + radius, z1 - radius, 450],
  ]
  return corners.flatMap(([cx, cz, a0]) =>
    Array.from({ length: 13 }, (_, i) => {
      const a = a0 + i * 7.5
      const rad = (a * Math.PI) / 180
      return [cx + radius * Math.cos(rad), cz + radius * Math.sin(rad), a]
    }),
  )
}

/** Keep the part of a convex outline on one side of world x = cut. */
function clipX(points, cut, keepLeft) {
  const inside = ([x]) => (keepLeft ? x <= cut : x >= cut)
  return points.flatMap((p, i) => {
    const q = points[(i + 1) % points.length]
    const out = inside(p) ? [p] : []
    if (inside(p) !== inside(q)) {
      const t = (cut - p[0]) / (q[0] - p[0])
      out.push([cut, p[1] + t * (q[1] - p[1])])
    }
    return out
  })
}

const OUTER = roundedRect(0, 14)
const INNER = roundedRect(6, 8)
const toStage = (points) => points.map(([x, z]) => onFace([x, z]))

export const CAP_OUTLINE = path(toStage(OUTER))
export const CAP_FACE = path(toStage(INNER))

/* The crown's flanks: the left one faces the key light, the right turns away. */
export const CAP_LIT = path(toStage(clipX(INNER, 1082, true)))
export const CAP_TURN = path(toStage(clipX(INNER, 1142, false)))

/* Glint on the crest of the rolled edge: bright along the lit edges (left and
   front), dimmer where the roll turns away (back and right). */
const arc = (points, from, to) => points.filter(([, , a]) => a >= from && a <= to)
export const CAP_GLINT = path(toStage([...arc(INNER, 495, 540), ...arc(INNER, 180, 315)]), false)
export const CAP_GLINT_DIM = path(toStage(arc(INNER, 315, 495)), false)

/* Walls: the outline's front-facing chain (from the leftmost silhouette point,
   angle 190.2°, round the front to the rightmost one at 10.2°) dropped to
   the nest. The front wall turns into the side wall mid-corner (315°). */
function wall(points, drop) {
  const top = points.map(([x, z]) => onFace([x, z]))
  const bottom = points.map(([x, z]) => (drop === null ? P(x, BASE, z) : onFace([x, z], drop)))
  return path([...top, ...reverse(bottom)])
}
const FRONT = arc(OUTER, 190, 315)
const SIDE = arc(OUTER, 315, 371)
export const CAP_WALL_FRONT = wall(FRONT, null)
export const CAP_WALL_SIDE = wall(SIDE, null)
export const CAP_ROLL_FRONT = wall(FRONT, 8)
export const CAP_ROLL_SIDE = wall(SIDE, 8)
const footLine = (points) => points.map(([x, z]) => P(x, BASE + 3, z))
export const CAP_FLANGE = path([
  ...footLine([...FRONT, ...SIDE]),
  ...reverse([...FRONT, ...SIDE].map(([x, z]) => P(x, BASE, z))),
])

/* Flat contact shadow on the nest (footprint offset +12, -4). */
export const CAP_SHADOW = path(OUTER.map(([x, z]) => P(x, BASE, z)).map(([x, y]) => [x + 12, y - 4]))

/* The crown at the pad's contact line (z 80), stage coordinates: the air
   clip's floor across the cap, and the sole strip just above it. */
const [ARC_L, ARC_R, ARC_C] = [P(X0, capH(X0), 80), P(X1, capH(X1), 80), P(XC, TOP, 80)]
const CTRL = `${r1(ARC_C[0])} ${r1(2 * ARC_C[1] - ARC_L[1])}`
export const CROWN_ARC = { left: ARC_L, right: ARC_R, ctrl: CTRL }
